import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import {
  BlogCategoryId,
  BlogPostDto,
  BlogPostPayload,
  BlogTagDto,
  Notification,
  BLOG_CATEGORY_FILTERS,
} from '../types';
import BlogRichTextEditor from '../components/BlogRichTextEditor';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { isPlatformSystemAdmin } from '../utils/roleUtils';

function estimateReadMinutes(html: string): number {
  const text = String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return 1;
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 225));
}

function slugifyTitle(title: string): string {
  return (
    String(title || '')
      .toLowerCase()
      .trim()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 72) || 'untitled-post'
  );
}

function parseTagsInput(raw: string): BlogTagDto[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((label) => ({ label, tone: 'blue' }));
}

/** Treat editor HTML as empty if there is no real text (TipTap may leave `<p><br></p>`). */
function isBodyHtmlEffectivelyEmpty(html: string): boolean {
  const text = String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return !text;
}

const defaultBodyHtml = `<h2>Overview</h2>
<p>AACore Booking lets a business create services, publish a hosted booking website, accept appointments, and communicate clearly with customers.</p>
<p>This guide explains how to set up your first services so customers know what they can book.</p>`;

const cardShell: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: '14px',
  boxShadow: '0 1px 3px rgba(0,0,0,.04)',
};

const BlogCreatePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug: editSlug } = useParams<{ slug: string }>();
  const isEditMode = Boolean(editSlug);
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  const [bodyHtml, setBodyHtml] = useState<string>(() => (isEditMode ? '' : defaultBodyHtml));

  const [title, setTitle] = useState('How to set up services in AACore Booking');
  const [category, setCategory] = useState<BlogCategoryId>('getting-started');
  const [badge, setBadge] = useState('Business Setup');
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'internal'>('public');
  const [authorDisplayName, setAuthorDisplayName] = useState('AACore Team');
  const [publishedAt, setPublishedAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [featuredImageUrl, setFeaturedImageUrl] = useState('');
  const [featuredImagePreviewUrl, setFeaturedImagePreviewUrl] = useState('');
  const [thumbIconClass, setThumbIconClass] = useState('bi-journal-text');
  const [summary, setSummary] = useState(
    'A simple step by step guide to creating services, setting prices, and making your booking site ready for customers.'
  );
  const [tagsInput, setTagsInput] = useState('Booking, Services, Setup, Small Business');
  const [slugManual, setSlugManual] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [seoTitle, setSeoTitle] = useState('How to Set Up Services in AACore Booking');
  const [seoDescription, setSeoDescription] = useState(
    'Learn how to set up services, prices, staff, hours, and booking rules in AACore Booking.'
  );
  const [featured, setFeatured] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  const [editLoadStatus, setEditLoadStatus] = useState<'idle' | 'loading' | 'error' | 'ok'>(() =>
    isEditMode ? 'loading' : 'idle'
  );
  const [featuredImageUploading, setFeaturedImageUploading] = useState(false);

  const seoTitleTouched = useRef(false);
  const featuredImageFileInputRef = useRef<HTMLInputElement>(null);

  const FEATURED_IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,image/gif,image/webp';
  const FEATURED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  const handleFeaturedImageFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!FEATURED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Please choose a JPEG, PNG, GIF, or WebP image.');
      return;
    }
    const maxBytes = 50 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error('Image must be 50 MB or smaller.');
      return;
    }

    const contentType = file.type || 'image/jpeg';
    setFeaturedImageUploading(true);
    try {
      const { uploads } = await apiService.getBlogFeaturedPresignedUploadUrls([contentType]);
      const slot = uploads[0];
      if (!slot?.uploadUrl) throw new Error('Could not start upload.');
      const putRes = await fetch(slot.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType }
      });
      if (!putRes.ok) throw new Error('Upload failed.');
      setFeaturedImageUrl(slot.finalUrl);
      setFeaturedImagePreviewUrl(slot.displayUrl || slot.finalUrl);
      toast.success('Featured image uploaded');
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
      toast.error(ax?.response?.data?.error ?? ax?.response?.data?.message ?? ax?.message ?? 'Upload failed');
    } finally {
      setFeaturedImageUploading(false);
    }
  };

  useEffect(() => {
    if (user?.firstName || user?.lastName) {
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      if (name) setAuthorDisplayName(name);
    }
  }, [user?.firstName, user?.lastName]);

  useEffect(() => {
    if (!isEditMode || !editSlug || !user?.id || !isPlatformSystemAdmin(user)) {
      return;
    }
    let cancelled = false;
    (async () => {
      setEditLoadStatus('loading');
      try {
        const post: BlogPostDto = await apiService.getAdminBlogPostBySlug(editSlug);
        if (cancelled) return;
        const categoryOk = BLOG_CATEGORY_FILTERS.some((c) => c.id !== 'all' && c.id === post.category);
        const categoryNext = (categoryOk ? post.category : 'getting-started') as BlogCategoryId;
        const vis = post.visibility;
        const visibilityNext =
          vis === 'public' || vis === 'unlisted' || vis === 'internal' ? vis : 'public';

        setEditingPostId(post.id);
        setTitle(post.title);
        setCategory(categoryNext);
        setBadge(post.badge ?? '');
        setVisibility(visibilityNext);
        setAuthorDisplayName(post.authorDisplayName || 'AACore Team');
        setPublishedAt(
          post.publishedAt ? new Date(post.publishedAt).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10)
        );
        setFeaturedImageUrl(post.featuredImageUrl ?? '');
        setFeaturedImagePreviewUrl(post.featuredImageDisplayUrl || post.featuredImageUrl || '');
        setThumbIconClass(post.thumbIconClass?.trim() || 'bi-journal-text');
        setSummary(post.summary || '');
        setTagsInput((post.tags || []).map((t) => t.label).join(', '));
        setSlugTouched(true);
        setSlugManual(post.slug);
        seoTitleTouched.current = true;
        setSeoTitle(post.seoTitle?.trim() || post.title);
        setSeoDescription(post.seoDescription ?? '');
        setFeatured(!!post.featured);
        setBodyHtml(post.bodyHtml || '');
        setEditLoadStatus('ok');
      } catch {
        if (!cancelled) setEditLoadStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditMode, editSlug, user?.id]);

  const editorHtmlSnapshot = bodyHtml;

  const wordCount = useMemo(() => {
    const text = editorHtmlSnapshot
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) return 0;
    return text.split(/\s+/).filter(Boolean).length;
  }, [editorHtmlSnapshot]);

  const readTime = useMemo(() => estimateReadMinutes(editorHtmlSnapshot), [editorHtmlSnapshot]);

  const htmlKb = useMemo(() => {
    const b = new Blob([editorHtmlSnapshot]).size / 1024;
    return Math.max(0.1, b).toFixed(1);
  }, [editorHtmlSnapshot]);

  const derivedSlug = useMemo(() => {
    if (slugTouched && slugManual.trim()) return slugifyTitle(slugManual);
    return slugifyTitle(title);
  }, [title, slugManual, slugTouched]);

  useEffect(() => {
    const loadNotifications = async () => {
      if (!user?.id) {
        setUnreadNotifications([]);
        return;
      }
      try {
        const response = await apiService.getNotifications();
        const notifications = response.data || [];
        setUnreadNotifications(notifications.filter((n: Notification) => !n.is_read));
      } catch {
        setUnreadNotifications([]);
      }
    };
    loadNotifications();
  }, [user?.id]);

  const canManageBlogPosts = Boolean(user && isPlatformSystemAdmin(user));

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {
      console.error(e);
    }
  };

  const switchToHtml = () => setHtmlMode(true);

  const switchToRich = () => setHtmlMode(false);

  const submitPost = async () => {
    if (!user || !canManageBlogPosts) {
      toast.error('Sign in with a platform administrator account to publish.');
      return;
    }
    const postBody = bodyHtml.trim();
    if (isBodyHtmlEffectivelyEmpty(postBody)) {
      toast.error('Please add some content to the post body.');
      return;
    }
    if (!title.trim() || title.trim().length < 3) {
      toast.error('Please enter a title.');
      return;
    }

    const tags = parseTagsInput(tagsInput);
    const payload: BlogPostPayload = {
      title: title.trim(),
      category,
      badge: badge.trim() || null,
      summary: summary.trim(),
      bodyHtml: postBody,
      tags,
      featuredImageUrl: featuredImageUrl.trim() || null,
      thumbIconClass: thumbIconClass.trim() || null,
      authorDisplayName: authorDisplayName.trim() || 'Author',
      status: 'PUBLISHED',
      visibility,
      featured,
      seoTitle: seoTitle.trim() || null,
      seoDescription: seoDescription.trim() || null,
      slug: derivedSlug,
      publishedAt: publishedAt ? new Date(publishedAt + 'T12:00:00').toISOString() : null,
    };

    try {
      setSaving(true);
      let result: BlogPostDto;
      if (editingPostId) {
        result = await apiService.updateBlogPost(editingPostId, payload);
      } else {
        result = await apiService.createBlogPost(payload);
      }
      toast.success(editingPostId ? 'Post updated' : 'Post published');
      if (result.visibility === 'internal') {
        navigate('/blog/', { replace: true });
      } else {
        navigate(`/blog/${result.slug}/`, { replace: true });
      }
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as any).response?.data?.error || (e as any).response?.data?.message
          : null;
      toast.error(msg || 'Could not save post');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    const redirectTarget = encodeURIComponent(`${location.pathname}${location.search || ''}`);
    return (
      <Navigate
        to={`/login?type=platform&redirect=${redirectTarget}`}
        replace
        state={{ from: location.pathname }}
      />
    );
  }

  if (!canManageBlogPosts) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
        <CustomerNavbar
          onLogout={handleLogout}
          showDashboardLink
          showLogoutButton
          showAuthButtons
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
        />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md text-center p-6 rounded-xl border bg-white" style={cardShell}>
            <p className="mb-3" style={{ fontSize: '1rem', color: '#334155' }}>
              Creating blog posts requires a platform administrator account.
            </p>
            <div className="mt-3">
              <Link to="/blog/" className="text-sm text-[#0B4778] underline">
                Back to blog
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (isEditMode && editLoadStatus === 'loading') {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
        <CustomerNavbar
          onLogout={handleLogout}
          showDashboardLink
          showLogoutButton
          showAuthButtons
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
        />
        <div className="flex-1 flex justify-center items-center py-24">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  if (isEditMode && editLoadStatus === 'error') {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
        <CustomerNavbar
          onLogout={handleLogout}
          showDashboardLink
          showLogoutButton
          showAuthButtons
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
        />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md text-center p-6 rounded-xl border bg-white" style={cardShell}>
            <p className="mb-3" style={{ fontSize: '1rem', color: '#334155' }}>
              Could not open this post for editing. It may be unavailable or your session may have expired.
            </p>
            <Link to="/blog/" className="aacore-btn-primary inline-flex justify-center">
              Back to blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f7fb]">
      <CustomerNavbar
        onLogout={handleLogout}
        showDashboardLink
        showLogoutButton
        showAuthButtons
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />

      <main className="flex-1 py-4">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 w-full">
          <section
            className="p-4 mb-4"
            style={{
              background: 'linear-gradient(135deg, #ffffff, #eef6ff)',
              border: '1px solid #dbe3ef',
              borderRadius: '18px',
              boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)',
            }}
          >
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
              <div>
                <p className="mb-2 uppercase tracking-wider" style={{ fontSize: '12px', fontWeight: 700, color: '#0B4778' }}>
                  Blog authoring
                </p>
                <h1 className="mb-2" style={{ fontSize: '1.25rem', fontWeight: 700 }}>
                  {isEditMode ? 'Edit Blog Post' : 'Create New Blog Post'}
                </h1>
                <p className="mb-0" style={{ fontSize: '1rem', color: '#334155' }}>
                  Write with the rich text editor, then switch to HTML whenever you need custom markup or embed code before publishing.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/blog/" className="aacore-btn-outlined justify-center">
                  <i className="bi bi-arrow-left" aria-hidden="true" /> Back to Blog
                </Link>
                <button
                  type="button"
                  className="aacore-btn-secondary justify-center"
                  onClick={() => {
                    void navigator.clipboard.writeText(bodyHtml).then(() => toast.success('HTML copied'));
                  }}
                >
                  <i className="bi bi-code-slash" aria-hidden="true" /> Copy HTML
                </button>
                <button
                  type="button"
                  className="aacore-btn-primary justify-center"
                  disabled={saving}
                  onClick={() => submitPost()}
                >
                  <i className="bi bi-send" aria-hidden="true" /> Publish Post
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
              {[
                { label: 'Words', value: String(wordCount) },
                { label: 'Min read', value: String(readTime) },
                { label: 'HTML size', value: `${htmlKb} KB` },
              ].map((s) => (
                <div key={s.label} className="p-3 rounded-xl border bg-white" style={{ borderColor: '#e5e7eb' }}>
                  <strong className="block text-lg" style={{ color: '#0B4778' }}>
                    {s.value}
                  </strong>
                  <span className="text-xs font-semibold" style={{ color: '#64748b' }}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <div className="flex flex-col lg:flex-row gap-4 items-start">
            <div className="w-full lg:flex-1 min-w-0">
              <form
                className="p-4"
                style={{ ...cardShell, borderRadius: '18px' }}
                onSubmit={(e) => e.preventDefault()}
              >
                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Badge (optional)</label>
                  <input
                    className="form-control w-full rounded-lg border border-gray-300 text-sm"
                    placeholder="e.g. Product Guide"
                    value={badge}
                    onChange={(e) => setBadge(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="post-title" className="block text-sm font-semibold text-gray-800 mb-1">
                    Post Title
                  </label>
                  <input
                    id="post-title"
                    className="form-control w-full rounded-lg border border-gray-300 text-sm"
                    value={title}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTitle(v);
                      if (!seoTitleTouched.current) setSeoTitle(v);
                    }}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Category</label>
                    <select
                      className="form-select w-full rounded-lg border border-gray-300 text-sm"
                      value={category}
                      onChange={(e) => setCategory(e.target.value as BlogCategoryId)}
                    >
                      {BLOG_CATEGORY_FILTERS.filter((c) => c.id !== 'all').map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Visibility</label>
                    <select
                      className="form-select w-full rounded-lg border border-gray-300 text-sm"
                      value={visibility}
                      onChange={(e) => setVisibility(e.target.value as typeof visibility)}
                    >
                      <option value="public">Public</option>
                      <option value="unlisted">Unlisted</option>
                      <option value="internal">Internal Only</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Author display name</label>
                    <input
                      className="form-control w-full rounded-lg border border-gray-300 text-sm"
                      value={authorDisplayName}
                      onChange={(e) => setAuthorDisplayName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-1">Publish date</label>
                    <input
                      type="date"
                      className="form-control w-full rounded-lg border border-gray-300 text-sm"
                      value={publishedAt}
                      onChange={(e) => setPublishedAt(e.target.value)}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Featured image URL</label>
                  <input
                    ref={featuredImageFileInputRef}
                    type="file"
                    accept={FEATURED_IMAGE_ACCEPT}
                    className="hidden"
                    aria-label="Featured image file"
                    onChange={handleFeaturedImageFileSelected}
                  />
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-stretch mt-1">
                    <input
                      className="form-control flex-1 min-w-0 rounded-lg border border-gray-300 text-sm"
                      placeholder="https://..."
                      value={featuredImageUrl}
                      onChange={(e) => setFeaturedImageUrl(e.target.value)}
                    />
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 shrink-0 sm:self-auto disabled:opacity-50"
                      disabled={featuredImageUploading}
                      aria-label="Upload featured image"
                      onClick={() => featuredImageFileInputRef.current?.click()}
                    >
                      {featuredImageUploading ? 'Uploading…' : 'Upload'}
                    </button>
                  </div>
                  {featuredImagePreviewUrl ? (
                    <img
                      src={featuredImagePreviewUrl}
                      alt=""
                      className="mt-2 w-full max-h-48 object-cover rounded-lg border border-gray-200"
                    />
                  ) : null}
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Thumb icon (Bootstrap Icons class)</label>
                  <input
                    className="form-control w-full rounded-lg border border-gray-300 text-sm"
                    placeholder="bi-journal-text"
                    value={thumbIconClass}
                    onChange={(e) => setThumbIconClass(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Short summary</label>
                  <textarea
                    className="form-control w-full rounded-lg border border-gray-300 text-sm"
                    rows={3}
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Tags (comma-separated)</label>
                  <input
                    className="form-control w-full rounded-lg border border-gray-300 text-sm"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                  />
                </div>

                <div className="mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-800">Post body</span>
                  <div className="inline-flex rounded-full border p-0.5 bg-gray-100" style={{ borderColor: '#d5dfec' }}>
                    <button
                      type="button"
                      className={`px-3 py-1.5 text-sm font-medium rounded-full border-0 ${
                        !htmlMode ? 'bg-white shadow-sm text-[#0B4778]' : 'bg-transparent text-gray-600'
                      }`}
                      onClick={switchToRich}
                    >
                      Rich Text
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1.5 text-sm font-medium rounded-full border-0 ${
                        htmlMode ? 'bg-white shadow-sm text-[#0B4778]' : 'bg-transparent text-gray-600'
                      }`}
                      onClick={switchToHtml}
                    >
                      HTML
                    </button>
                  </div>
                </div>

                {!htmlMode ? (
                  <BlogRichTextEditor
                    key={isEditMode ? editingPostId ?? 'edit-loading' : 'new'}
                    value={bodyHtml}
                    onChange={setBodyHtml}
                  />
                ) : (
                  <textarea
                    value={bodyHtml}
                    onChange={(e) => setBodyHtml(e.target.value)}
                    className="form-control w-full rounded-xl border border-gray-300 text-sm font-mono min-h-[360px]"
                    spellCheck={false}
                    aria-label="Post body HTML"
                  />
                )}

                <div className="flex flex-wrap gap-2 mt-4">
                  <button
                    type="button"
                    className="aacore-btn-primary"
                    disabled={saving}
                    onClick={() => submitPost()}
                  >
                    <i className="bi bi-send" /> Publish Post
                  </button>
                </div>

                <label className="flex items-center gap-2 mt-3 text-sm">
                  <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} />
                  Feature this post on the blog home
                </label>
              </form>
            </div>

            <aside className="w-full lg:w-80 shrink-0 flex flex-col gap-3 lg:sticky lg:top-24">
              <div className="p-4" style={cardShell}>
                <h3 className="mb-2" style={{ fontSize: '1rem', fontWeight: 600 }}>
                  SEO
                </h3>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">SEO title</label>
                  <input
                    className="form-control w-full rounded-lg border border-gray-300 text-sm"
                    value={seoTitle}
                    onChange={(e) => {
                      seoTitleTouched.current = true;
                      setSeoTitle(e.target.value);
                    }}
                  />
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Meta description</label>
                  <textarea
                    className="form-control w-full rounded-lg border border-gray-300 text-sm"
                    rows={3}
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                  />
                </div>
                <div>
                  <div className="flex justify-between gap-2 items-center mb-1">
                    <label className="text-xs font-semibold text-gray-700 mb-0">URL slug</label>
                    <button
                      type="button"
                      className="aacore-btn-outlined px-2 py-1 text-xs"
                      onClick={() => {
                        setSlugTouched(false);
                        setSlugManual('');
                      }}
                    >
                      Auto
                    </button>
                  </div>
                  <input
                    className="form-control w-full rounded-lg border border-gray-300 text-sm"
                    value={slugTouched ? slugManual : derivedSlug}
                    onChange={(e) => {
                      setSlugTouched(true);
                      setSlugManual(e.target.value);
                    }}
                  />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogCreatePage;
